import { defineComponent, h, Teleport } from "vue";
import { SkywardException } from "#skyward_error";
import type { VueContext } from "../../types";
import type {
    CombinedFieldProps, DataFieldProps, FieldComponent, FormattedFieldProps, RelationshipFieldProps, TreeBrowseEvents,
    TreeBrowseProps,
    TreeBrowseSection
} from "../../types/skyward/treebrowse";
import { createAppWithRootComponent, VueEmit } from "../core/vueHelpers";
import { AggregateField } from "./aggregateField";
import { CalculatedField } from "./calculatedField";
import * as Classes from "./classes";
import { DataField } from "./dataField";
import { EnvironmentVariableField } from "./environmentVariableField";
import * as Events from "./events";
import List, { CellSlotArguments, getColumns } from "./list";
import { RelationshipField } from "./relationshipField";


export interface TreeBrowseContext extends VueContext
{
    props?: TreeBrowseProps,
    events?: TreeBrowseEvents
}

type CombinedInstance = (InstanceType<typeof DataField> | InstanceType<typeof EnvironmentVariableField> | InstanceType<typeof RelationshipField> | InstanceType<typeof CalculatedField> | InstanceType<typeof AggregateField>);
type FieldInstance = InstanceType<FieldComponent>;

interface CommonFormattedFields
{
    isTreeBrowseRow: boolean;
    fieldKey: string;
};
type FormattedFieldType<T> = T extends CombinedInstance ? ReturnType<T["formatField"]> & T["field"] & CommonFormattedFields : never;
type InstanceFormattedField = FormattedFieldType<CombinedInstance>;
export type FormattedRelationshipField = FormattedFieldType<InstanceType<typeof RelationshipField>>;
export type FormattedDataField = FormattedFieldType<InstanceType<typeof DataField>>;


const components =
{
    "dataField": DataField,
    "relationshipField": RelationshipField,
    "environmentVariableField": EnvironmentVariableField,
    "calculatedField": CalculatedField,
    "aggregateField": AggregateField
};

const defaultProps: Required<TreeBrowseProps> =
{
    showHeader: true,
    showParametersColumn: false,
    showEyeColumn: false,
    sections: [] as TreeBrowseSection[],
    additionalSections: [] as TreeBrowseSection[],
    onRowMounted: (_rowElement: HTMLElement): void => { }
};

const TreeBrowseListComponent = defineComponent(
{
    name: "treeBrowse",
    props: { },
    emits:
    {
        recordDoubleClick: VueEmit<(data: FormattedFieldProps) => boolean>(),
        recordSelected: VueEmit<(rowData: FormattedFieldProps) => boolean>(),
    },
    computed:
    {
        allSections(): TreeBrowseSection[] { return this.sections.concat(this.additionalSections); },
        selectedField(): CombinedFieldProps | undefined
        {
            return this.__selectedRecord?.field as CombinedFieldProps | undefined;
        },
        __selectedRecord(): FieldInstance | undefined
        {
            return this.__allRecordsByFieldKey.get(this.selectedFieldKey!);
        },
        modifiedFields(): ReadonlyMap<string, CombinedFieldProps> { return this.__modifiedFields; }
    },
    data()
    {
        return {
            selectedFieldKey: undefined as string | undefined,
            __allRecordsByFieldKey: new Map<string, FieldInstance>(),
            __modifiedFields: new Map<string, CombinedFieldProps>(),
            ...defaultProps,
        };
    },
    watch:
    {
        onRowMounted: function (newValue)
        {
            if (newValue)
            {
                this.__allRecordsByFieldKey.forEach((value) =>
                {
                    newValue(value.$el as HTMLElement);
                });
            }
        },
        __selectedRecord:
        {
            handler: function (newValue, oldValue)
            {
                // If the current selected field has been removed, clear the selection so it won't still be selected if it gets added back in
                if (oldValue && !newValue)
                {
                    this.selectedFieldKey = undefined;
                }
            },
            // wait until after render, when __allRecords has been updated
            flush: "post"
        },
    },
    render()
    {
        const list: (any | null)[] = [];

        const anyRowHasShield = this.allSections.some((section) => section.rows?.some((row) => (row as DataFieldProps).showShield));
        const anyRowHasDescription = this.allSections.some((section) => section.rows?.some((row) => (row as DataFieldProps).hasDescription));

        const columns = getColumns({ showEyeColumn: this.showEyeColumn, anyRowHasShield, anyRowHasDescription, showParametersColumn: this.showParametersColumn });
        const columnCount = columns.reduce((a, b) => a + b.columns.length, 0);

        for (let sectionIndex = 0; sectionIndex < this.allSections.length; sectionIndex += 1)
        {
            const section = this.allSections[sectionIndex];
            if (!section.rows)
            {
                continue;
            }

            const component = typeof section.component === "string" ? (components[section.component]! as unknown as FieldComponent) : section.component;
            const rows = section.rows!.map((row) =>
            {
                const fieldKey = component.fieldKey(row);
                const modifiedRow = this.__modifiedFields.get(fieldKey);
                return h(component,
                    {
                        key: `${section.key}-${fieldKey}`,
                        ref: (elem) =>
                        {
                            if (elem)
                            {
                                this.__allRecordsByFieldKey.set(fieldKey, elem as FieldInstance);
                            }
                            else
                            {
                                this.__allRecordsByFieldKey.delete(fieldKey);
                            }
                        },
                        field: modifiedRow ?? row,
                        originalField: row,
                        selected: this.selectedFieldKey === fieldKey,
                        onRecordSelected: () => this.__handleRecordSelected(fieldKey),
                        onRecordDoubleClick: () => this.$emit(Events.recordDoubleClick, this.__formatFieldData(fieldKey)! as FormattedFieldProps),
                        onRecordMounted: (rowElement) => this.onRowMounted?.call(this, rowElement),
                    },
                    {
                        default: (cellSlotArguments: CellSlotArguments) => columns.flatMap(c => c.columns).map(c => c(cellSlotArguments))
                    }
                );
            });

            if (section.container)
            {
                list.push(
                    <Teleport to={section.container} key={section.key}>
                        <List
                            showHeader={false}
                            columns={columns}
                        >
                            {this.__buildTitle(section, columnCount)}
                            {rows}
                        </List>
                    </Teleport>
                );
            }
            else
            {
                if (list.length > 0 && section.rows.length)
                {
                    list.push(this.__buildTitle(section, columnCount));
                }
                list.push(...rows);
            }
        }

        return (<List
            showHeader={this.showHeader}
            columns={columns}
        >
            {list}
        </List>);
    },
    methods:
    {
        selectRecord(fieldKey: string | undefined | null)
        {
            let selectedRow: FieldInstance | undefined;
            let selectedFieldKey = fieldKey;
            let foundField = false;

            if (fieldKey !== null)
            {
                if (arguments.length === 0)
                {
                    console.warn("Calling treebrowse.selectRecord(undefined) is deprecated. Use selectFirstRecord() instead.");
                }
                selectedRow = this.__allRecordsByFieldKey.get(fieldKey!);

                foundField = selectedRow !== undefined;
                if (foundField)
                {
                    $(selectedRow!.$el).scrollintoview({ direction: "vertical" });
                }
                else
                {
                    if (fieldKey !== undefined)
                    {
                        window.skyward.error.reportException(new SkywardException(`Could not find field ${fieldKey} on treebrowse.`));
                    }
                    // We always want to have a record selected if there are any.
                    return { ...this.selectFirstRecord(), foundField: false };
                }
            }
            else
            {
                foundField = true;
                selectedRow = undefined;
                selectedFieldKey = undefined;
            }

            this.selectedFieldKey = selectedFieldKey!;
            return { foundField, element: selectedRow?.$el, rowData: this.__formatRecordData(selectedRow) };
        },
        selectFirstRecord()
        {
            let foundField = false;
            let selectedRow: FieldInstance | undefined;
            let selectedFieldKey: string | undefined;
            const selectedSection = this.allSections.find(section => (section.rows?.length ?? 0) > 0);
            if (selectedSection)
            {
                const component = typeof selectedSection.component === "string" ? components[selectedSection.component]! : selectedSection.component;
                selectedFieldKey = component.fieldKey(selectedSection.rows![0]);
                selectedRow = this.__allRecordsByFieldKey.get(selectedFieldKey!);
                if (selectedRow)
                {
                    foundField = true;
                    $(selectedRow!.$el).scrollintoview({ direction: "vertical" });
                }
            }
            this.selectedFieldKey = selectedFieldKey;
            return { foundField, element: selectedRow?.$el, rowData: this.__formatRecordData(selectedRow) };
        },
        getSelectedElement()
        {
            return this.__selectedRecord?.$el as HTMLElement;
        },
        getSelectedFieldData()
        {
            return this.__formatRecordData(this.__selectedRecord);
        },
        clearSelectedRecord()
        {
            this.selectedFieldKey = undefined;
        },
        getFieldData(element: HTMLElement | string)
        {
            const record = typeof element === "string"
                ? this.__allRecordsByFieldKey.get(element)
                : Array.from(this.__allRecordsByFieldKey.values()).find(row => row.$el === element);
            return this.__formatRecordData(record);
        },
        getFieldElement(element: string)
        {
            const record = this.__allRecordsByFieldKey.get(element);
            return record?.$el as HTMLElement | undefined;
        },
        __handleRecordSelected(fieldKey: string)
        {
            this.selectedFieldKey = fieldKey;
            this.$emit(Events.recordSelected, this.getSelectedFieldData()! as FormattedFieldProps);
        },
        __formatRecordData(record: FieldInstance | undefined)
        {
            if (!record)
            {
                return undefined;
            }
            const componentSpecificData = record.formatField();
            const result =
            {
                ...record.field,
                ...componentSpecificData,
                // Used by reporting.js createNewColumn()
                isTreeBrowseRow: true,
                fieldKey: record.fieldKey(),
            } as InstanceFormattedField & { isTreeBrowseRow: boolean; };
            return result;
            },
        modifyField(fieldKey: string, value: CombinedFieldProps)
        {
            const record = this.__allRecordsByFieldKey.get(fieldKey);
            if (window.skyward.global.deepEquals(record?.originalField ?? null, value))
            {
                this.__modifiedFields.delete(fieldKey);
            }
            else
            {
                this.__modifiedFields.set(fieldKey, value);
            }
        },
        __formatFieldData(fieldKey: string | undefined)
        {
            const record = this.__allRecordsByFieldKey.get(fieldKey!);
            return this.__formatRecordData(record);
        },
        __buildTitle(section: TreeBrowseSection, colspan: number)
        {
            return section.title
                ? <tr><td class={Classes.treeListItemHeader} colspan={colspan}>{section.title}</td></tr>
                : null;
        }
    }
});

export const TreeBrowseList =
{
    buildVue(context: TreeBrowseContext)
    {
        const treeBrowse = createAppWithRootComponent(TreeBrowseListComponent, context.events).mount(context.container!);
        Object.assign(treeBrowse, defaultProps, context.props ?? {});
        return treeBrowse;
    }
};

export function isRelationshipField(field: FormattedFieldProps): field is FormattedRelationshipField;
export function isRelationshipField(field: CombinedFieldProps): field is RelationshipFieldProps;
export function isRelationshipField(field: any): boolean
{
    return !!field.isRelationship;
}

export function isDataField(field: FormattedFieldProps): field is FormattedDataField
{
    return (typeof (field as DataFieldProps).isOperation) === "boolean";
}